import React, { FC, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { Trans } from 'react-i18next'
import t from '../../../../translations/main'
import storage from 'src/components/Core/Utils/storage'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'
import { toBase64 } from 'src/components/Core/Utils/global'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import { useApplicationSelected, useProspectsApplications, useProspectsApplicationsUpdate } from 'src/components/Core/Context/OnboardingContext'

import SendComment from './Components/SendComment'
import Font from 'src/components/Core/Typography/Font/Font'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import Popup from 'src/components/Atoms/Popup/Popup'
import CommentBaloon from './Components/CommentBalloon'

import {
  CommentsPageWrapper,
  CommentsSection,
  TitleSection,
} from './GeneralMessage.styles'
import { ChildComments } from '../Chat/types'

const GeneralMessage: FC = () => {
  const [comments, setComments] = useState<ChildComments[]>([])
  const [fileData, setFileData] = useState({
    name: '',
    data: null
  })
  const [emptyMessageError, setEmptyMessageError] = useState(false)
  const [virusFileError, setVirusFileError] = useState({error: false, message: ''})
  const [fetchingStatus, setFetchingStatus] = useState<'fetching' | 'fetchedNoComments' | 'fetched' | 'error'>('fetching')
  const bottomRef = useRef(null)
  const prospectsApplications = useProspectsApplications()
  const setProspectApplications = useProspectsApplicationsUpdate()
  const applicationSelected = useApplicationSelected()

  const { sendRequest, error } = useFetch()

  const userName: string = storage.get(storage?.keys?.main?.user)?.name
  const applicationId: string = storage.get(storage?.keys?.onboarding?.applicationId)
  const sendComment = async (comment: string) => {
    const payload = {
      applicationId,
      username: userName,
      stepIndex: '-1',
      commentContent: comment,
      fileBase64Contents: fileData.data ? await toBase64(fileData.data) : ''
    }

    const modifyResponse = ({commentID, fileID}: {commentID: number, fileID: number}) => {
      (document.getElementById('fileInput') as HTMLInputElement).value = null
      document.querySelector('textarea').value = ''
      if(commentID){
        (document.getElementById('fileInput') as HTMLInputElement).value = null
        setFileData({name: '', data: null})
        setFetchingStatus('fetched')
        setComments(prev => {
          const temp = [...prev]
          temp.push({
            commentID: commentID,
            ...payload,
            commentContent: comment,
            fileID: fileID || 0,
            fileName: fileData.name,
            isCommentRead: 'true',
            commentDateTime: new Date(),
            self: true,
            parentCommentid: 0
          })
          return temp
        })
      } else {
        setFetchingStatus('error')
      }
    }
    if(fileData?.data){
      const data = new FormData()
      // @ts-ignore:next-line
      data.append('inputFile', fileData?.data, 'file')
      sendRequest(
        {
          scope: 'onboarding',
          url: `${getBaseApiHost()}/AccountOpeningForms/virus_scan_file`,
          method: 'POST',
          body: data,
          headers: {
            'Accept': 'text/plain',
          },
          noStringfyBody: true
        },
        data => {
          if(!data?.cleanResult) {
            (document.getElementById('fileInput') as HTMLInputElement).value = null
            setFileData({name: '', data: null})
            // @ts-ignore:next-line
            setVirusFileError({error: true, message: <Trans i18nKey={'components.inputFiles.fileWithVirus'} values={{ filename: fileData?.name }}/>})
          } else {
            sendRequest({
              scope: 'onboarding',
              url: import.meta.env.VITE_MOCK_DATA !== 'true'
                ? `${getBaseApiHost()}/Comments/addComment`
                : `${window.location.origin}/mock/comments-post-withFile.json`,
              method: 'POST',
              body: {...payload, filename: fileData?.name},
            },
            modifyResponse
            )
          }
        }
      )
    } else {
      sendRequest({
        scope: 'onboarding',
        url: import.meta.env.VITE_MOCK_DATA !== 'true'
          ? `${getBaseApiHost()}/Comments/addComment`
          : `${window.location.origin}/mock/comments-post-withFile.json`,
        method: 'POST',
        body: {...payload, filename: fileData?.name},
      },
      modifyResponse
      )
    }
  }

  useEffect(() => {
    try {
      import.meta.env.VITE_MOCK_DATA !== 'true'
        ? sendRequest({
          scope: 'onboarding',
          url:  `${getBaseApiHost()}/Comments/getComments?applicationId=${prospectsApplications[`${applicationSelected}`]?.applicationId}&stepIndex=-1`
        },
        data => {
          setComments(data)
          setFetchingStatus(data.length > 0 ? 'fetched' : 'fetchedNoComments')
          if(data?.length > 0) {
            markMessagesAsRead(data)
          }
        })
        : setTimeout(() =>
          sendRequest({
            scope: 'onboarding',
            url: `${window.location.origin}/mock/comments-get-general.json`,
          },
          data => {
            setComments(data)
            setFetchingStatus(data.length > 0 ? 'fetched' : 'fetchedNoComments')
          }),1000)

    } catch (error) {
      setFetchingStatus('error')
    }

  },[prospectsApplications[`${applicationSelected}`]?.applicationId])

  const markMessagesAsRead = (data?:ChildComments[]) => {
    if(!data || data.length === 0) return
    const unreadCommentsId = data.filter(comment => comment.isCommentRead?.toLowerCase?.() === 'false').map(unreadComment => unreadComment?.commentID)
    if(unreadCommentsId.length > 0) {
      sendRequest({
        scope: 'onboarding',
        url: import.meta.env.VITE_MOCK_DATA !== 'true'
          ? `${getBaseApiHost()}/Comments/markcommentasread`
          : `${window.location.origin}/mock/postMessage.json`,
        method: 'POST',
        body: {
          applicationID: applicationId,
          commentIds: unreadCommentsId
        }
      },
      data => {
        if(data) {
          // @ts-ignore:next-line
          setProspectApplications(prev => {
            const temp = [...prev]
            temp[`${applicationSelected as number}`].unreadCommentsCount = 0
            return temp
          })
        }
      }
      )
    }
  }

  useEffect(() => {
    if(comments && comments.length > 0) bottomRef.current?.scrollIntoView()
  }, [comments])

  useEffect(() => {
    if(error) setFetchingStatus('error')
  },[error])

  if(fetchingStatus === 'fetching') return <Spinner />

  if(fetchingStatus === 'error') return <Font margin='10% 0 0 0'>{t('onboarding.generalMessage.errorFetching')}</Font>

  return (
    <CommentsPageWrapper flexDirection='column' justifyContent='space-between'>
      <TitleSection addBottomBorder gap='10px'>
        <Font tag='h2'>
          {t('onboarding.generalMessage.title', { userName })}
        </Font>
        <Font>{moment().format('dddd D MMMM YYYY')}</Font>
      </TitleSection>
      <CommentsSection>
        {
          fetchingStatus === 'fetched' &&
          comments.length > 0
            ? comments.map(comment => <CommentBaloon comment={comment} applicationId={applicationId} key={comment.commentID}/>)
            : <Font tag='h3'>{t('onboarding.generalMessage.noComments')}</Font>
        }
        <div ref={bottomRef} />
      </CommentsSection>
      <SendComment
        setFileData={setFileData}
        fileData={fileData}
        setEmptyMessageError={setEmptyMessageError}
        sendComment={sendComment}
      />
      {
        emptyMessageError &&
        <Popup
          type='warning'
          label={t('onboarding.generalMessage.emptyMessage')}
          popupCloseHelperFunction={() => setEmptyMessageError(false)}
        />
      }
      {
        virusFileError.error &&
        <Popup
          removeSelfClose
          type='error'
          label={virusFileError.message}
          popupCloseHelperFunction={() => setVirusFileError({error: false, message: ''})}
          click={() => setVirusFileError({error: false, message: ''})}
        />
      }
    </CommentsPageWrapper>
  )
}

export default GeneralMessage
